/**
 * The contract between `EField` and whatever control sits inside it.
 *
 * `EField` owns the ids for the label, hint and error text and publishes them
 * here; every control in this library injects the context and wires
 * `id`, `aria-describedby`, `aria-invalid` and `aria-required` from it. That is
 * what makes an RFC 9457 field error (CLAUDE.md §7) land on the correct control
 * with the correct announcement, without every form re-deriving id plumbing.
 *
 * A control used outside `EField` injects `null` and falls back to its own id,
 * so nothing here is mandatory.
 */

import { inject, provide, type ComputedRef, type InjectionKey } from 'vue'

export interface FieldContext {
  /** id to put on the control itself; the `<label for>` points at it. */
  controlId: ComputedRef<string>
  /** Space-separated ids of hint and error text, or `undefined`. */
  describedBy: ComputedRef<string | undefined>
  invalid: ComputedRef<boolean>
  required: ComputedRef<boolean>
  disabled: ComputedRef<boolean>
}

export const FIELD_CONTEXT_KEY: InjectionKey<FieldContext> = Symbol('erpx.field')

export function provideFieldContext(context: FieldContext): void {
  provide(FIELD_CONTEXT_KEY, context)
}

export function useFieldContext(): FieldContext | null {
  return inject(FIELD_CONTEXT_KEY, null)
}
